import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import InvestmentBreakdownPanel from "@/components/InvestmentBreakdownPanel";
import UomSelect, { uomPriceFactor, uomConvertValue } from "@/components/UomSelect";
import MasterCombobox from "@/components/MasterCombobox";
import {
    Tooltip, ResponsiveContainer, Cell,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

// Cost breakup chart palette — friendlier, no red, no black. Indigo + teal +
// amber + violet + steel-blue + lime.
const CHART_COLORS = ["#002FA7", "#0EA5E9", "#F59E0B", "#2A9D8F", "#9C27B0", "#84CC16"];

export default function DraftEditor({ draft, materials, bopMaster, vendors, machines, manpower, totals, part, partId, onSave, onRename, onRefreshMasters }) {
    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    // Substitute the per-mode value when active draft is ZBC. A master row can
    // carry an optional `_zbc` shadow value (e.g. `price_per_unit_zbc`). When the
    // shadow is set AND the active draft is in ZBC mode, the picker shows and
    // copies that value instead of the default. Otherwise the default value is
    // used. This lets ONE master row carry both Quotation and 0-Based prices.
    const isZbc = (draft?.mode || "quotation") === "zbc";
    const swap = (row, defaultKey, zbcKey) => {
        const z = row[zbcKey];
        if (isZbc && z !== null && z !== undefined && z !== "") {
            return { ...row, [defaultKey]: Number(z) || 0 };
        }
        return row;
    };
    const materialsForMode = materials.map((m) => swap(m, "price_per_unit", "price_per_unit_zbc"));
    const bopForMode = (bopMaster || []).map((b) => swap(b, "price_per_unit", "price_per_unit_zbc"));
    const vendorsForMode = (vendors || []).map((v) => swap(v, "price_per_piece", "price_per_piece_zbc"));
    const manpowerForMode = manpower.map((mp) => swap(swap(mp, "rate_per_hour", "rate_per_hour_zbc"), "salary_per_month", "salary_per_month_zbc"));
    const machinesForMode = machines.map((mc) => {
        const z = mc.mhr_rs_per_hr_zbc;
        if (isZbc && z !== null && z !== undefined && z !== "") {
            return { ...mc, breakdown: { ...(mc.breakdown || {}), mhr_rs_per_hr: Number(z) || 0 } };
        }
        return mc;
    });
    const t = totals || {
        material_total: 0, process_total: 0, outsource_total: 0, total_investment: 0,
        amortised_investment_per_piece: 0, icc: 0, sga: 0, packing: 0, transportation: 0,
        overhead_fixed: 0, profit_value: 0, overhead_total: 0, final_cost: 0,
    };

    // ----- RM (Raw Materials) -----
    const addMaterial = () => onSave({ materials: [...(draft.materials || []), { material_id: "", name: "", unit: "", unit_other: "", price_per_unit: 0, quantity: 1, gross_weight: 0, scrap_weight: 0, net_weight: 0 }] });
    const updateMaterial = (i, patch) => { const next = [...draft.materials]; next[i] = { ...next[i], ...patch }; onSave({ materials: next }); };
    // For the "Weight UOM" of a material/BOP line, pick a sensible default that
    // matches typical manufacturing thinking: parts are priced per-kg (master) but
    // consumed in grams (line). Same for length: per-m master, per-mm line.
    // Returns the user-friendly weight UOM that pairs with a given price UOM.
    const smartWeightUom = (priceUnit) => {
        const u = (priceUnit || "").toLowerCase();
        if (u === "kg" || u === "ton") return "g";
        if (u === "m") return "mm";
        if (u === "m²") return "mm²";
        if (u === "m³") return "L";
        return priceUnit || "";
    };

    const pickMaterial = (i, mid /*, item */) => {
        // Ignore the lightweight `item` from MasterCombobox (only id+label+sub) —
        // look up the FULL master entry to get unit, price, etc.
        const m = materialsForMode.find((x) => x.material_id === mid);
        if (!m) return;
        const next = [...draft.materials]; next[i] = { ...next[i], material_id: mid, name: m.name, unit: m.unit, unit_other: m.unit_other || "", weight_unit: smartWeightUom(m.unit), weight_unit_other: m.unit_other || "", price_per_unit: m.price_per_unit };
        onSave({ materials: next });
    };
    const createMaterialFromName = async (label) => {
        const r = await api.post("/materials", { name: label, unit: "kg", price_per_unit: 0 });
        onRefreshMasters?.();
        return { id: r.data.material_id, label: r.data.name, ...r.data };
    };
    // UOM change → auto-convert price within the same family (kg↔g↔mg↔ton, etc.)
    const changeMaterialUom = (i, patch) => {
        const next = [...draft.materials];
        const prevUnit = next[i].unit || "";
        const nextUnit = patch.unit ?? prevUnit;
        const factor = uomPriceFactor(prevUnit, nextUnit);
        const newPrice = factor !== 1 ? round2(Number(next[i].price_per_unit || 0) * factor) : next[i].price_per_unit;
        next[i] = { ...next[i], ...patch, price_per_unit: newPrice };
        onSave({ materials: next });
    };
    const removeMaterial = (i) => onSave({ materials: draft.materials.filter((_, idx) => idx !== i) });

    // ----- BOP (Bought-Out Parts) -----
    const addBop = () => onSave({ bop: [...(draft.bop || []), { bop_id: "", name: "", vendor: "", quantity: 1, price_per_unit: 0, unit: "pcs", unit_other: "" }] });
    const updateBop = (i, patch) => { const next = [...(draft.bop || [])]; next[i] = { ...next[i], ...patch }; onSave({ bop: next }); };
    const changeBopUom = (i, patch) => {
        const next = [...(draft.bop || [])];
        const prevUnit = next[i].unit || "";
        const nextUnit = patch.unit ?? prevUnit;
        const factor = uomPriceFactor(prevUnit, nextUnit);
        const newPrice = factor !== 1 ? round2(Number(next[i].price_per_unit || 0) * factor) : next[i].price_per_unit;
        next[i] = { ...next[i], ...patch, price_per_unit: newPrice };
        onSave({ bop: next });
    };
    const pickBop = (i, bid /*, item */) => {
        const b = (bopMaster || []).find((x) => x.bop_id === bid);
        if (!b) return;
        const u = b.unit || "pcs";
        const next = [...(draft.bop || [])]; next[i] = { ...next[i], bop_id: bid, name: b.name, vendor: b.vendor, price_per_unit: b.price_per_unit, unit: u, unit_other: b.unit_other || "", weight_unit: smartWeightUom(u), weight_unit_other: b.unit_other || "" };
        onSave({ bop: next });
    };
    const createBopFromName = async (label) => {
        const r = await api.post("/bop", { name: label, vendor: "", price_per_unit: 0, unit: "pcs" });
        onRefreshMasters?.();
        return { id: r.data.bop_id, label: r.data.name, ...r.data };
    };
    const removeBop = (i) => onSave({ bop: (draft.bop || []).filter((_, idx) => idx !== i) });

    // ----- Processes (inline) -----
    // Update process line cost rule to use qty
    const addProcess = () => onSave({ processes: [...(draft.processes || []), { name: "", machine_id: "", machine_name: "", mhr_rs_per_hr: 0, manpower_id: "", manpower_count: 1, manpower_rate_per_hr_total: 0, cycle_time_sec: 0, quantity: 1 }] });
    const updateProc = (i, patch) => { const next = [...draft.processes]; next[i] = { ...next[i], ...patch }; onSave({ processes: next }); };
    const pickMachine = (i, machine_id, item) => {
        const next = [...draft.processes];
        if (machine_id === "__na__") {
            next[i] = { ...next[i], machine_id: "__na__", machine_name: "N/A", mhr_rs_per_hr: 0 };
        } else {
            const m = machines.find((x) => x.machine_id === machine_id);
            next[i] = { ...next[i], machine_id: machine_id || "", machine_name: m?.name || "", mhr_rs_per_hr: m?.breakdown?.mhr_rs_per_hr || 0 };
        }
        onSave({ processes: next });
    };
    const createMachineFromName = async (label) => {
        const r = await api.post("/machines", { name: label, inputs: {} });
        onRefreshMasters?.();
        return { id: r.data.machine_id, label: r.data.name, ...r.data };
    };
    const pickManpower = (i, manpower_id, item) => {
        const next = [...draft.processes];
        if (manpower_id === "__na__") {
            next[i] = { ...next[i], manpower_id: "__na__", manpower_count: 0, manpower_rate_per_hr_total: 0 };
        } else {
            const mp = manpower.find((x) => x.manpower_id === manpower_id);
            const count = Number(next[i].manpower_count) || 1;
            next[i] = { ...next[i], manpower_id: manpower_id || "", manpower_count: count || 1, manpower_rate_per_hr_total: Math.round((mp?.rate_per_hour || 0) * count * 100) / 100 };
        }
        onSave({ processes: next });
    };
    const createManpowerFromName = async (label) => {
        const r = await api.post("/manpower", { category: label, salary_per_month: 0, hrs_per_month: 200 });
        onRefreshMasters?.();
        return { id: r.data.manpower_id, label: r.data.category, ...r.data };
    };
    const setManpowerCount = (i, count) => {
        const c = Number(count) || 0;
        const next = [...draft.processes];
        if (next[i].manpower_id === "__na__") {
            next[i] = { ...next[i], manpower_count: c, manpower_rate_per_hr_total: 0 };
        } else {
            const mp = manpower.find((x) => x.manpower_id === next[i].manpower_id);
            next[i] = { ...next[i], manpower_count: c, manpower_rate_per_hr_total: Math.round((mp?.rate_per_hour || 0) * c * 100) / 100 };
        }
        onSave({ processes: next });
    };
    const removeProc = (i) => onSave({ processes: draft.processes.filter((_, idx) => idx !== i) });
    const moveProc = (i, dir) => {
        const arr = [...(draft.processes || [])];
        const j = i + dir;
        if (j < 0 || j >= arr.length) return;
        [arr[i], arr[j]] = [arr[j], arr[i]];
        onSave({ processes: arr });
    };

    // ----- Outsource -----
    const addOutsource = () => onSave({ outsource: [...(draft.outsource || []), { process_name: "", vendor: "", vendor_id: "", price_per_unit: 0, price_per_piece: 0, quantity: 1, unit: "pcs", unit_other: "" }] });
    const updateOutsource = (i, patch) => { const next = [...(draft.outsource || [])]; next[i] = { ...next[i], ...patch }; onSave({ outsource: next }); };
    const changeOutsourceUom = (i, patch) => {
        const next = [...(draft.outsource || [])];
        const prevUnit = next[i].unit || "pcs";
        const nextUnit = patch.unit ?? prevUnit;
        const factor = uomPriceFactor(prevUnit, nextUnit);
        const prevPrice = Number(next[i].price_per_unit || next[i].price_per_piece || 0);
        const newPrice = factor !== 1 ? round2(prevPrice * factor) : prevPrice;
        next[i] = { ...next[i], ...patch, price_per_unit: newPrice };
        onSave({ outsource: next });
    };
    const pickVendor = (i, vid /*, item */) => {
        const v = (vendors || []).find((x) => x.vendor_id === vid);
        if (!v) return;
        const next = [...(draft.outsource || [])];
        next[i] = { ...next[i], vendor_id: vid, vendor: v.name, process_name: next[i].process_name || v.process, price_per_unit: v.price_per_piece };
        onSave({ outsource: next });
    };
    const createVendorFromName = async (label) => {
        const r = await api.post("/vendors", { name: label, process: "", price_per_piece: 0 });
        onRefreshMasters?.();
        return { id: r.data.vendor_id, label: r.data.name, ...r.data };
    };
    const removeOutsource = (i) => onSave({ outsource: (draft.outsource || []).filter((_, idx) => idx !== i) });

    // ----- Investment -----
    // Codes are auto-generated as INV-001, INV-002, … from row position (always synced on save)
    const invCode = (i) => `INV-${String(i + 1).padStart(3, "0")}`;
    const remapInvCodes = (list) => list.map((inv, i) => ({ ...inv, code: invCode(i) }));
    const addInvestment = () => onSave({ investments: remapInvCodes([...(draft.investments || []), { name: "", category: "Machine", value: 0 }]) });
    const updateInvestment = (i, patch) => { const next = [...(draft.investments || [])]; next[i] = { ...next[i], ...patch }; onSave({ investments: remapInvCodes(next) }); };
    const removeInvestment = (i) => onSave({ investments: remapInvCodes((draft.investments || []).filter((_, idx) => idx !== i)) });

    // ----- Overhead -----
    const updateOverhead = (patch) => onSave({ overhead: { ...(draft.overhead || {}), ...patch } });

    // Waterfall data: each step adds to the cumulative
    const waterfallData = useMemo(() => {
        const steps = [
            { name: "RM", value: t.rm_total ?? 0, color: "#002FA7", own: t.own_rm_total ?? t.rm_total ?? 0, child: t.child_rm_total ?? 0 },
            { name: "BOP", value: t.bop_total ?? 0, color: "#5B6FE0", own: t.own_bop_total ?? t.bop_total ?? 0, child: t.child_bop_total ?? 0 },
            { name: "Process", value: t.process_total, color: "#0EA5E9", own: t.own_process_total ?? t.process_total, child: t.child_process_total ?? 0 },
            { name: "Outsource", value: t.outsource_total, color: "#264653", own: t.own_outsource_total ?? t.outsource_total, child: t.child_outsource_total ?? 0 },
            { name: "Investment", value: t.amortised_investment_per_piece, color: "#9C27B0", own: t.own_amortised_per_piece ?? t.amortised_investment_per_piece, child: t.child_amortised_per_piece ?? 0 },
            { name: "Overhead", value: t.overhead_fixed, color: "#FFB703", own: t.own_overhead_fixed ?? t.overhead_fixed, child: t.child_overhead_fixed ?? 0 },
            { name: "Profit", value: t.profit_value, color: "#2A9D8F", own: t.own_profit_value ?? t.profit_value, child: t.child_profit_value ?? 0 },
        ].filter((s) => s.value > 0);
        let running = 0;
        const out = steps.map((s) => {
            const item = { ...s, base: running, cumulative: running + s.value };
            running += s.value;
            return item;
        });
        out.push({ name: "Total", base: 0, value: running, color: "#84CC16", own: running, child: 0, cumulative: running, isTotal: true });
        return out;
    }, [t]);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Draft Name</Label>
                    <Input
                        className="max-w-xs rounded-none"
                        value={draft.name}
                        onChange={(e) => onSave({ name: e.target.value })}
                        onBlur={(e) => onRename(e.target.value)}
                        data-testid="draft-name-input"
                    />
                </div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 border border-zinc-200 px-3 py-1.5">
                    <Label htmlFor="direct-cost-mode" className="cursor-pointer">Direct cost entry (legacy parts)</Label>
                    <Switch
                        id="direct-cost-mode"
                        checked={!!draft.direct_cost_mode}
                        onCheckedChange={(v) => onSave({ direct_cost_mode: !!v })}
                        data-testid="direct-cost-mode-switch"
                    />
                </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
                <KPI label="Material / piece" value={t.material_total} accent="#002FA7" testid="kpi-material" />
                <KPI label="Process / piece" value={t.process_total} accent="#0EA5E9" testid="kpi-process" />
                <KPI label="Outsource / piece" value={t.outsource_total} accent="#264653" testid="kpi-outsource" />
                <KPI label="Overhead / piece" value={t.overhead_total} accent="#FFB703" testid="kpi-overhead" />
            </div>

            {/* Sub-assemblies (read-only) */}
            {part.is_assembly && (
                <Section title="0. Sub-assemblies (Child Parts)" action={<span className="text-[10px] text-zinc-400 uppercase tracking-wider">Per-child draft selectable below</span>}>
                    <SubAssembliesPanel
                        partId={part.part_id}
                        draftId={draft.draft_id}
                        overrides={draft.child_draft_overrides || {}}
                        onChangeOverride={(childPartId, childDraftId) => {
                            const next = { ...(draft.child_draft_overrides || {}) };
                            if (childDraftId) next[childPartId] = childDraftId;
                            else delete next[childPartId];
                            onSave({ child_draft_overrides: next });
                        }}
                    />
                </Section>
            )}

            {draft.direct_cost_mode ? (
                <Section title="Direct Cost Entry" action={<span className="text-[10px] text-zinc-400 uppercase tracking-wider">Bypasses BOM line items</span>}>
                    <div className="grid grid-cols-4 gap-3">
                        <FixedField label="RM total / piece (₹)" v={draft.direct_rm_total ?? 0} onChange={(v) => onSave({ direct_rm_total: v })} testid="direct-rm-total" />
                        <FixedField label="BOP total / piece (₹)" v={draft.direct_bop_total ?? 0} onChange={(v) => onSave({ direct_bop_total: v })} testid="direct-bop-total" />
                        <FixedField label="Process total / piece (₹)" v={draft.direct_process_total ?? 0} onChange={(v) => onSave({ direct_process_total: v })} testid="direct-process-total" />
                        <FixedField label="Outsource total / piece (₹)" v={draft.direct_outsource_total ?? 0} onChange={(v) => onSave({ direct_outsource_total: v })} testid="direct-outsource-total" />
                    </div>
                    <p className="text-[11px] text-zinc-500 font-mono mt-3">Investment, Overhead and Profit below still apply on top of these lump-sums.</p>
                </Section>
            ) : null}

            {/* MATERIAL — RM + BOP */}
            {!draft.direct_cost_mode && (
            <Section
                title="1. Material Cost"
                action={
                    <div className="flex items-center gap-4 text-xs uppercase tracking-wider">
                        <span className="text-zinc-400">
                            RM ₹{(totals?.rm_total ?? 0).toFixed(2)} · BOP ₹{(totals?.bop_total ?? 0).toFixed(2)}
                        </span>
                    </div>
                }
            >
                {/* RM */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs uppercase tracking-wider text-zinc-500 font-mono">1A · Raw Material (RM)</h4>
                        <button onClick={addMaterial} className="text-xs uppercase tracking-wider text-[#002FA7] hover:underline" data-testid="add-material-line">+ Add RM</button>
                    </div>
                    <table className="w-full sheet-table" data-testid="material-lines-table">
                        <thead><tr><th>Material</th><th>Price UOM</th><th>Price / Unit</th><th>Weight UOM</th><th>Gross Wt</th><th>Scrap Wt</th><th>Net Wt</th><th>Qty</th><th>Line Cost (₹)</th><th></th></tr></thead>
                        <tbody>
                            {(draft.materials || []).map((m, i) => {
                                const grossEntered = Number(m.gross_weight) > 0;
                                const netOrScrapEntered = Number(m.net_weight) > 0 || Number(m.scrap_weight) > 0;
                                const price = Number(m.price_per_unit) || 0;
                                const qRaw = m.quantity;
                                const qty = (qRaw && Number(qRaw) > 0) ? Number(qRaw) : 1;
                                const priceUnit = m.unit || "";
                                const weightUnit = m.weight_unit || priceUnit;
                                // Effective gross weight (direct OR net+scrap), in the weightUnit
                                const eg = grossEntered
                                    ? Number(m.gross_weight)
                                    : (netOrScrapEntered ? (Number(m.net_weight) || 0) + (Number(m.scrap_weight) || 0) : 0);
                                // Convert to price UOM then compute line cost
                                let lineCost;
                                if (eg > 0) {
                                    const egInPriceUnit = uomConvertValue(eg, weightUnit, priceUnit);
                                    lineCost = qty * egInPriceUnit * price;
                                } else if (qRaw && Number(qRaw) > 0) lineCost = Number(qRaw) * price;
                                else lineCost = 0;
                                return (
                                    <tr key={i}>
                                        <td className="min-w-[200px]">
                                            <MasterCombobox
                                                items={materialsForMode.map((x) => ({ id: x.material_id, label: x.name, sub: `₹${x.price_per_unit}/${x.unit}` }))}
                                                value={m.material_id || ""}
                                                placeholder="Pick / type to add"
                                                displayLabel={m.name}
                                                onChange={(id, item) => pickMaterial(i, id, item)}
                                                onCreate={createMaterialFromName}
                                                testIdPrefix={`material-pick-${i}`}
                                            />
                                        </td>
                                        <td className="min-w-[110px]">
                                            <UomSelect unit={m.unit || ""} unitOther={m.unit_other || ""} onChange={(patch) => changeMaterialUom(i, patch)} testIdPrefix={`material-uom-${i}`} />
                                        </td>
                                        <td><Input type="number" step="0.01" value={m.price_per_unit} onChange={(e) => updateMaterial(i, { price_per_unit: e.target.value })} className="h-8 rounded-none font-mono w-24" data-testid={`material-price-${i}`} /></td>
                                        <td className="min-w-[110px]">
                                            <UomSelect
                                                unit={m.weight_unit || m.unit || ""}
                                                unitOther={m.weight_unit_other || ""}
                                                onChange={(patch) => updateMaterial(i, { weight_unit: patch.unit, weight_unit_other: patch.unit_other })}
                                                testIdPrefix={`material-weight-uom-${i}`}
                                            />
                                        </td>
                                        <td><Input type="number" step="0.001" value={m.gross_weight ?? 0} onChange={(e) => updateMaterial(i, { gross_weight: e.target.value })} disabled={netOrScrapEntered} title={netOrScrapEntered ? "Clear Net/Scrap to enter Gross" : ""} className={`h-8 rounded-none font-mono w-24 ${netOrScrapEntered ? "bg-zinc-100 text-zinc-400" : ""}`} data-testid={`material-gross-${i}`} /></td>
                                        <td><Input type="number" step="0.001" value={m.scrap_weight ?? 0} onChange={(e) => updateMaterial(i, { scrap_weight: e.target.value })} disabled={grossEntered} className={`h-8 rounded-none font-mono w-24 ${grossEntered ? "bg-zinc-100 text-zinc-400" : ""}`} data-testid={`material-scrap-${i}`} /></td>
                                        <td><Input type="number" step="0.001" value={m.net_weight ?? 0} onChange={(e) => updateMaterial(i, { net_weight: e.target.value })} disabled={grossEntered} className={`h-8 rounded-none font-mono w-24 ${grossEntered ? "bg-zinc-100 text-zinc-400" : ""}`} data-testid={`material-net-${i}`} /></td>
                                        <td><Input type="number" step="0.01" value={m.quantity ?? 1} onChange={(e) => updateMaterial(i, { quantity: e.target.value })} className="h-8 rounded-none font-mono w-20" data-testid={`material-qty-${i}`} /></td>
                                        <td className="font-mono font-medium" data-testid={`material-line-cost-${i}`}>
                                            <div className="flex items-center gap-1">
                                                <span>₹ {lineCost.toFixed(2)}</span>
                                                {/* Sanity guard — if user typed a "small" value while weight UOM still matches
                                                    a "big" price UOM (e.g. 37 with weight_unit=kg, price_unit=kg) the line cost
                                                    explodes 1000×. We show an explicit warning + one-click fix to switch the
                                                    Weight UOM to the natural pair (g for kg-priced, mm for m-priced, …). */}
                                                {(() => {
                                                    if (!(eg > 0 && weightUnit === priceUnit)) return null;
                                                    const map = { kg: "g", ton: "kg", m: "mm", "m²": "mm²", "m³": "L" };
                                                    const suggest = map[priceUnit];
                                                    if (!suggest) return null;
                                                    if (lineCost < 1000 && eg < 5) return null; // line below ₹1k or weight <5 → likely OK
                                                    return (
                                                        <button
                                                            type="button"
                                                            title={`This row's cost looks ${lineCost > 50000 ? "very high" : "high"}. If "${eg}" was meant in ${suggest}, click to switch Weight UOM.`}
                                                            onClick={() => updateMaterial(i, { weight_unit: suggest })}
                                                            className="text-[9px] uppercase tracking-wider bg-[#FFB703] text-zinc-900 px-1.5 py-0.5 hover:bg-[#FFC83D]"
                                                            data-testid={`material-fix-uom-${i}`}
                                                        >
                                                            uom ?
                                                        </button>
                                                    );
                                                })()}
                                            </div>
                                            {(eg > 0 || (qRaw && Number(qRaw) > 0)) && (
                                                <div className="text-[9px] text-zinc-400 font-normal mt-0.5 leading-tight" data-testid={`material-line-math-${i}`}>
                                                    {eg > 0 ? (
                                                        weightUnit !== priceUnit
                                                            ? `${qty} × ${eg} ${weightUnit} → ${uomConvertValue(eg, weightUnit, priceUnit).toFixed(4)} ${priceUnit} × ₹${price}/${priceUnit}`
                                                            : `${qty} × ${eg} ${priceUnit} × ₹${price}/${priceUnit}`
                                                    ) : `${qRaw} × ₹${price}`}
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-right w-10"><button onClick={() => removeMaterial(i)} className="text-zinc-400 hover:text-[#E63946]"><Trash2 size={14} /></button></td>
                                    </tr>
                                );
                            })}
                            {(draft.materials || []).length === 0 && <tr><td colSpan={10} className="text-center text-zinc-400 italic py-4">No raw material lines.</td></tr>}
                        </tbody>
                    </table>
                </div>

                {/* BOP */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs uppercase tracking-wider text-zinc-500 font-mono">1B · Bought-Out Parts (BOP)</h4>
                        <button onClick={addBop} className="text-xs uppercase tracking-wider text-[#E63946] hover:underline" data-testid="add-bop-line">+ Add BOP</button>
                    </div>
                    <table className="w-full sheet-table" data-testid="bop-lines-table">
                        <thead><tr><th>Part</th><th>Vendor</th><th>UOM</th><th>Qty</th><th>Price / Unit</th><th>Line Cost (₹)</th><th></th></tr></thead>
                        <tbody>
                            {(draft.bop || []).map((b, i) => {
                                const q = Number(b.quantity) || 0;
                                const price = Number(b.price_per_unit) || 0;
                                const lineCost = q * price;
                                return (
                                    <tr key={i}>
                                        <td className="min-w-[200px]">
                                            <MasterCombobox
                                                items={bopForMode.map((x) => ({ id: x.bop_id, label: x.name, sub: `₹${x.price_per_unit}/${x.unit || "pcs"}` }))}
                                                value={b.bop_id || ""}
                                                placeholder="Pick / type to add"
                                                displayLabel={b.name}
                                                onChange={(id, item) => pickBop(i, id, item)}
                                                onCreate={createBopFromName}
                                                testIdPrefix={`bop-pick-${i}`}
                                            />
                                        </td>
                                        <td><Input value={b.vendor || ""} onChange={(e) => updateBop(i, { vendor: e.target.value })} placeholder="Vendor" className="h-8 rounded-none w-40" data-testid={`bop-vendor-${i}`} /></td>
                                        <td className="min-w-[120px]">
                                            <UomSelect
                                                unit={b.unit || "pcs"}
                                                unitOther={b.unit_other || ""}
                                                onChange={(patch) => changeBopUom(i, patch)}
                                                testIdPrefix={`bop-uom-${i}`}
                                            />
                                        </td>
                                        <td><Input type="number" step="0.01" value={b.quantity ?? 0} onChange={(e) => updateBop(i, { quantity: e.target.value })} className="h-8 rounded-none font-mono w-20" data-testid={`bop-qty-${i}`} /></td>
                                        <td><Input type="number" step="0.01" value={b.price_per_unit ?? 0} onChange={(e) => updateBop(i, { price_per_unit: e.target.value })} className="h-8 rounded-none font-mono w-24" data-testid={`bop-price-${i}`} /></td>
                                        <td className="font-mono font-medium">₹ {lineCost.toFixed(2)}</td>
                                        <td className="text-right w-10"><button onClick={() => removeBop(i)} className="text-zinc-400 hover:text-[#E63946]"><Trash2 size={14} /></button></td>
                                    </tr>
                                );
                            })}
                            {(draft.bop || []).length === 0 && <tr><td colSpan={7} className="text-center text-zinc-400 italic py-4">No bought-out parts.</td></tr>}
                        </tbody>
                    </table>
                </div>

                <div className="text-right mt-3 font-mono text-sm">
                    Material subtotal: <span className="font-semibold">₹ {t.material_total.toFixed(2)}</span>
                </div>
            </Section>
            )}

            {/* PROCESS */}
            {!draft.direct_cost_mode && (
            <Section title="2. Process Cost" action={<button onClick={addProcess} className="text-xs uppercase tracking-wider text-[#002FA7] hover:underline" data-testid="add-process-line">+ Add line</button>}>
                <table className="w-full sheet-table" data-testid="process-lines-table">
                    <thead><tr><th></th><th>Process Name</th><th>Machine</th><th>Manpower</th><th>Rate (₹/hr)</th><th>Cycle (sec)</th><th>Process cost/pc</th><th>Qty</th><th>Line Cost</th><th></th></tr></thead>
                    <tbody>
                        {(draft.processes || []).map((p, i) => {
                            const rate = (Number(p.mhr_rs_per_hr) || 0) + (Number(p.manpower_rate_per_hr_total) || 0);
                            const perPiece = rate * (Number(p.cycle_time_sec) || 0) / 3600;
                            const qRaw = Number(p.quantity);
                            const qty = (!qRaw || qRaw <= 0) ? 1 : qRaw;
                            const lineCost = perPiece * qty;
                            return (
                                <tr key={i}>
                                    <td className="w-12">
                                        <div className="flex flex-col items-center gap-0.5">
                                            <button onClick={() => moveProc(i, -1)} disabled={i === 0} title="Move up" className="text-zinc-400 hover:text-[#002FA7] disabled:opacity-20 disabled:cursor-not-allowed leading-none" data-testid={`process-move-up-${i}`}>▲</button>
                                            <span className="text-[9px] font-mono text-zinc-400">{i + 1}</span>
                                            <button onClick={() => moveProc(i, +1)} disabled={i === (draft.processes || []).length - 1} title="Move down" className="text-zinc-400 hover:text-[#002FA7] disabled:opacity-20 disabled:cursor-not-allowed leading-none" data-testid={`process-move-down-${i}`}>▼</button>
                                        </div>
                                    </td>
                                    <td className="min-w-[140px]"><Input value={p.name || ""} onChange={(e) => updateProc(i, { name: e.target.value })} placeholder="e.g. Welding" className="h-8 rounded-none" data-testid={`process-name-${i}`} /></td>
                                    <td className="min-w-[170px]">
                                        <MasterCombobox
                                            items={[{ id: "__na__", label: "N/A — no machine" }, ...machinesForMode.map((x) => ({ id: x.machine_id, label: x.name, sub: `₹${x.breakdown.mhr_rs_per_hr}/hr` }))]}
                                            value={p.machine_id || ""}
                                            placeholder="Pick / type to add"
                                            displayLabel={p.machine_name}
                                            onChange={(id, item) => pickMachine(i, id, item)}
                                            onCreate={createMachineFromName}
                                            testIdPrefix={`process-machine-${i}`}
                                        />
                                    </td>
                                    <td className="min-w-[140px]">
                                        <MasterCombobox
                                            items={[{ id: "__na__", label: "N/A — no manpower" }, ...manpowerForMode.map((x) => ({ id: x.manpower_id, label: x.category, sub: `₹${x.rate_per_hour}/hr` }))]}
                                            value={p.manpower_id || ""}
                                            placeholder="Pick / type to add"
                                            onChange={(id, item) => pickManpower(i, id, item)}
                                            onCreate={createManpowerFromName}
                                            testIdPrefix={`process-manpower-${i}`}
                                        />
                                    </td>
                                    <td className="font-mono text-xs">₹ {rate.toFixed(2)}</td>
                                    <td><Input type="number" step="0.1" value={p.cycle_time_sec} onChange={(e) => updateProc(i, { cycle_time_sec: e.target.value })} className="h-8 rounded-none font-mono w-20" data-testid={`process-cycle-${i}`} /></td>
                                    <td className="font-mono text-xs">₹ {perPiece.toFixed(2)}</td>
                                    <td><Input type="number" step="0.01" value={p.quantity ?? 1} onChange={(e) => updateProc(i, { quantity: e.target.value })} className="h-8 rounded-none font-mono w-20" data-testid={`process-qty-${i}`} /></td>
                                    <td className="font-mono font-medium">₹ {lineCost.toFixed(2)}</td>
                                    <td className="text-right w-10"><button onClick={() => removeProc(i)} className="text-zinc-400 hover:text-[#E63946]"><Trash2 size={14} /></button></td>
                                </tr>
                            );
                        })}
                        {(draft.processes || []).length === 0 && <tr><td colSpan={10} className="text-center text-zinc-400 italic py-6">No process lines.</td></tr>}
                    </tbody>
                </table>
                <div className="text-right mt-2 font-mono text-sm">Subtotal: <span className="font-semibold">₹ {t.process_total.toFixed(2)}</span></div>
            </Section>
            )}

            {/* OUTSOURCE */}
            {!draft.direct_cost_mode && (
            <Section title="3. Outsource Cost" action={<button onClick={addOutsource} className="text-xs uppercase tracking-wider text-[#002FA7] hover:underline" data-testid="add-outsource-line">+ Add line</button>}>
                <table className="w-full sheet-table" data-testid="outsource-lines-table">
                    <thead><tr><th>Process</th><th>Vendor (from master)</th><th>UOM</th><th>Qty</th><th>Price / Unit (₹)</th><th>Line Cost (₹)</th><th></th></tr></thead>
                    <tbody>
                        {(draft.outsource || []).map((o, i) => {
                            const price = (Number(o.price_per_unit) || 0) > 0 ? Number(o.price_per_unit) : (Number(o.price_per_piece) || 0);
                            const qRaw = Number(o.quantity); const qty = (!qRaw || qRaw <= 0) ? 1 : qRaw;
                            const lineCost = price * qty;
                            return (
                                <tr key={i}>
                                    <td><Input value={o.process_name || ""} onChange={(e) => updateOutsource(i, { process_name: e.target.value })} placeholder="e.g. Plating" className="h-8 rounded-none" data-testid={`outsource-process-${i}`} /></td>
                                    <td className="min-w-[180px]">
                                        <MasterCombobox
                                            items={vendorsForMode.map((x) => ({ id: x.vendor_id, label: x.name, sub: `${x.process || ""} · ₹${x.price_per_piece}/pc` }))}
                                            value={o.vendor_id || ""}
                                            placeholder="Pick / type to add"
                                            displayLabel={o.vendor}
                                            onChange={(id, item) => pickVendor(i, id, item)}
                                            onCreate={createVendorFromName}
                                            testIdPrefix={`outsource-vendor-pick-${i}`}
                                        />
                                    </td>
                                    <td className="min-w-[120px]">
                                        <UomSelect unit={o.unit || "pcs"} unitOther={o.unit_other || ""} onChange={(patch) => changeOutsourceUom(i, patch)} testIdPrefix={`outsource-uom-${i}`} />
                                    </td>
                                    <td><Input type="number" step="0.01" value={o.quantity ?? 1} onChange={(e) => updateOutsource(i, { quantity: e.target.value })} className="h-8 rounded-none font-mono w-20" data-testid={`outsource-qty-${i}`} /></td>
                                    <td><Input type="number" step="0.01" value={price} onChange={(e) => updateOutsource(i, { price_per_unit: Number(e.target.value) || 0 })} className="h-8 rounded-none font-mono w-28" data-testid={`outsource-price-${i}`} /></td>
                                    <td className="font-mono font-medium">₹ {lineCost.toFixed(2)}</td>
                                    <td className="text-right w-10"><button onClick={() => removeOutsource(i)} className="text-zinc-400 hover:text-[#E63946]"><Trash2 size={14} /></button></td>
                                </tr>
                            );
                        })}
                        {(draft.outsource || []).length === 0 && <tr><td colSpan={7} className="text-center text-zinc-400 italic py-6">No outsource lines. Pick a vendor from the Vendor master.</td></tr>}
                    </tbody>
                </table>
                <div className="text-right mt-2 font-mono text-sm">Subtotal: <span className="font-semibold">₹ {t.outsource_total.toFixed(2)}</span></div>
            </Section>
            )}

            {/* INVESTMENT */}
            <Section title="4. Investment" action={
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
                        <Label htmlFor="amortise-switch" className="cursor-pointer">Amortise into per-piece cost</Label>
                        <Switch
                            id="amortise-switch"
                            checked={!!draft.amortise_investment}
                            onCheckedChange={(v) => onSave({ amortise_investment: !!v })}
                            data-testid="amortise-switch"
                        />
                    </div>
                    <button onClick={addInvestment} className="text-xs uppercase tracking-wider text-[#002FA7] hover:underline" data-testid="add-investment-line">+ Add line</button>
                </div>
            }>
                <table className="w-full sheet-table" data-testid="investment-lines-table">
                    <thead><tr><th style={{ width: 90 }}>Code</th><th>Item</th><th>Category</th><th>Value (₹)</th><th></th></tr></thead>
                    <tbody>
                        {(draft.investments || []).map((inv, i) => (
                            <tr key={i}>
                                <td className="font-mono text-xs text-zinc-600 uppercase" data-testid={`investment-code-${i}`}>{invCode(i)}</td>
                                <td><Input value={inv.name || ""} onChange={(e) => updateInvestment(i, { name: e.target.value })} placeholder="e.g. CNC fixture" className="h-8 rounded-none" data-testid={`investment-name-${i}`} /></td>
                                <td className="min-w-[140px]">
                                    <Select value={inv.category || "Machine"} onValueChange={(v) => updateInvestment(i, { category: v })}>
                                        <SelectTrigger className="rounded-none h-8"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Machine">Machine</SelectItem>
                                            <SelectItem value="Tool">Tool</SelectItem>
                                            <SelectItem value="Fixture">Fixture</SelectItem>
                                            <SelectItem value="Mould">Mould</SelectItem>
                                            <SelectItem value="Other">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </td>
                                <td><Input type="number" step="100" value={inv.value || 0} onChange={(e) => updateInvestment(i, { value: Number(e.target.value) || 0 })} className="h-8 rounded-none font-mono w-32" data-testid={`investment-value-${i}`} /></td>
                                <td className="text-right w-10"><button onClick={() => removeInvestment(i)} className="text-zinc-400 hover:text-[#E63946]"><Trash2 size={14} /></button></td>
                            </tr>
                        ))}
                        {(draft.investments || []).length === 0 && <tr><td colSpan={5} className="text-center text-zinc-400 italic py-6">No investment items yet.</td></tr>}
                    </tbody>
                </table>
                <div className="grid grid-cols-3 gap-3 mt-3 text-sm font-mono">
                    <div>Total Investment: <span className="font-semibold">₹ {t.total_investment.toLocaleString()}</span></div>
                    <div>Lifetime Volume: <span className="font-semibold">{((part.annual_volume || 0) * (part.project_years || 5)).toLocaleString()}</span> <span className="text-zinc-400">(vol × {part.project_years || 5} yrs)</span></div>
                    <div className="text-right">
                        Per-piece amortised: <span className="font-semibold">₹ {t.amortised_investment_per_piece.toFixed(2)}</span>
                        <div className="text-[10px] text-zinc-500 mt-1">{draft.amortise_investment ? "Included in part price" : "Shown separately (not in price)"}</div>
                    </div>
                </div>
            </Section>

            {/* OVERHEAD */}
            <Section title="5. Overhead Cost" action={
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
                    <Label htmlFor="seq-bases-switch" className="cursor-pointer">Sequential base calculation</Label>
                    <Switch
                        id="seq-bases-switch"
                        checked={draft.overhead?.sequential_bases !== false}
                        onCheckedChange={(v) => updateOverhead({ sequential_bases: !!v })}
                        data-testid="overhead-sequential-switch"
                    />
                </div>
            }>
                <div className="grid grid-cols-5 gap-3">
                    <FixedField label="Packing (₹)" v={draft.overhead?.packing ?? 0} onChange={(v) => updateOverhead({ packing: v })} testid="overhead-packing" />
                    <FixedField label="Transportation (₹)" v={draft.overhead?.transportation ?? 0} onChange={(v) => updateOverhead({ transportation: v })} testid="overhead-transportation" />
                    <DualField
                        label="ICC (Inventory)"
                        mode={draft.overhead?.icc_mode || "value"}
                        valueRs={draft.overhead?.icc_value ?? 0}
                        valuePct={draft.overhead?.icc_pct ?? 0}
                        onModeChange={(m) => updateOverhead({ icc_mode: m })}
                        onValueChange={(v) => updateOverhead({ icc_value: v })}
                        onPctChange={(v) => updateOverhead({ icc_pct: v })}
                        derived={t.icc}
                        testid="overhead-icc"
                    />
                    <DualField
                        label="Rejection"
                        mode={draft.overhead?.rejection_mode || "pct"}
                        valueRs={draft.overhead?.rejection_value ?? 0}
                        valuePct={draft.overhead?.rejection_pct ?? 0}
                        onModeChange={(m) => updateOverhead({ rejection_mode: m })}
                        onValueChange={(v) => updateOverhead({ rejection_value: v })}
                        onPctChange={(v) => updateOverhead({ rejection_pct: v })}
                        derived={t.rejection}
                        testid="overhead-rejection"
                    />
                    <DualField
                        label="Tool Maintenance"
                        mode={draft.overhead?.tool_maintenance_mode || "pct"}
                        valueRs={draft.overhead?.tool_maintenance_value ?? 0}
                        valuePct={draft.overhead?.tool_maintenance_pct ?? 0}
                        onModeChange={(m) => updateOverhead({ tool_maintenance_mode: m })}
                        onValueChange={(v) => updateOverhead({ tool_maintenance_value: v })}
                        onPctChange={(v) => updateOverhead({ tool_maintenance_pct: v })}
                        derived={t.tool_maintenance}
                        testid="overhead-tool-maintenance"
                    />
                    <DualField
                        label="SGA"
                        mode={draft.overhead?.sga_mode || "value"}
                        valueRs={draft.overhead?.sga_value ?? 0}
                        valuePct={draft.overhead?.sga_pct ?? 0}
                        onModeChange={(m) => updateOverhead({ sga_mode: m })}
                        onValueChange={(v) => updateOverhead({ sga_value: v })}
                        onPctChange={(v) => updateOverhead({ sga_pct: v })}
                        derived={t.sga}
                        testid="overhead-sga"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4 text-sm font-mono">
                    <div>Fixed overhead: <span className="font-semibold">₹ {t.overhead_fixed.toFixed(2)}</span></div>
                    <div className="text-right text-[10px] text-zinc-500" data-testid="overhead-base-description">
                        {draft.overhead?.sequential_bases !== false ? (
                            <>Sequential bases · A) ICC: RM+BOP · B) Rejection: RM+BOP+Process+Outsource+ICC · C) Tool Maint: RM+BOP+Process+Outsource+ICC+Rejection · D) SGA: RM+BOP+Process+Outsource+ICC+Rejection+Tool Maint · E) Profit: RM+BOP+Process+Outsource+ICC+Rejection+Tool Maint+SGA (all incl. children)</>
                        ) : (
                            <>Parallel bases (not sequential) · A) ICC: RM+BOP · B) Rejection: RM+BOP+Process+Outsource · C) Tool Maint: RM+BOP+Process+Outsource · D) SGA: RM+BOP+Process+Outsource · E) Profit: RM+BOP+Process+Outsource (all incl. children)</>
                        )}
                    </div>
                </div>
            </Section>

            <Section title="6. Profit">
                <div className="grid grid-cols-3 gap-3">
                    <DualField
                        label={t.mode === "zbc" ? "Profit (derived — read-only)" : "Profit"}
                        mode={draft.overhead?.profit_mode || "pct"}
                        valueRs={draft.overhead?.profit_value ?? 0}
                        valuePct={draft.overhead?.profit_pct ?? 0}
                        onModeChange={(m) => updateOverhead({ profit_mode: m })}
                        onValueChange={(v) => updateOverhead({ profit_value: v })}
                        onPctChange={(v) => updateOverhead({ profit_pct: v })}
                        derived={t.profit_value}
                        testid="overhead-profit"
                    />
                    <div className="text-sm font-mono">
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Effective profit / piece</div>
                        <div className={`text-2xl font-semibold ${(t.profit_value || 0) >= 0 ? "text-[#2A9D8F]" : "text-[#E63946]"}`}>
                            ₹ {(t.profit_value || 0).toFixed(2)} <span className="text-sm text-zinc-500">({((t.profit_value || 0) / Math.max(t.final_cost || 1, 0.0001) * 100).toFixed(1)}% of selling price)</span>
                        </div>
                    </div>
                    <div className="text-[11px] text-zinc-500 font-mono">
                        Margin shown = profit ÷ final selling price (same formula as the cost-breakup row). Profit input % is applied on {draft.overhead?.sequential_bases !== false ? "the sequential profit base = RM+BOP+Process+Outsource (own + children) + ICC + Rejection + Tool Maintenance + SGA" : "the parallel base = RM+BOP+Process+Outsource (own + children) only"}. {t.mode === "zbc" && <span className="block mt-1 text-[#9C27B0]">Actual costing mode: profit = frozen_price − all costs.</span>}
                    </div>
                </div>
            </Section>

            {/* Charts */}
            <div className="grid grid-cols-2 gap-6">
                <div className="border border-zinc-200 p-6">
                    <div className="tag-pill mb-3">Cost Breakup</div>
                    <h3 className="text-xl font-bold tracking-tight mb-1">Waterfall to total cost</h3>
                    <p className="text-xs text-zinc-500 mb-4 font-mono">Each step adds to the running total / piece</p>
                    <div style={{ height: 280 }}>
                        <ResponsiveContainer>
                            <BarChart data={waterfallData} margin={{ top: 16, right: 8, left: 8, bottom: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${v}`} />
                                <Tooltip
                                    cursor={{ fill: "rgba(0,0,0,0.04)" }}
                                    formatter={(val, name, ctx) => {
                                        if (name === "base") return null;
                                        const row = ctx?.payload;
                                        return [`₹ ${Number(val).toFixed(2)}`, row?.isTotal ? "Total" : "Adds"];
                                    }}
                                />
                                {/* invisible base bar to lift visible value */}
                                <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
                                <Bar dataKey="value" stackId="w" isAnimationActive={false}>
                                    {waterfallData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-4 space-y-1.5" data-testid="cost-breakup-waterfall">
                        {waterfallData.filter((d) => !d.isTotal).map((d) => {
                            const total = waterfallData.find((x) => x.isTotal)?.value || 1;
                            const pct = (d.value / total) * 100;
                            const showSplit = part.is_assembly && (d.child || 0) > 0;
                            return (
                                <div key={d.name} className="flex items-center gap-3 text-xs font-mono">
                                    <div className="w-3 h-3" style={{ background: d.color }} />
                                    <div className="flex-1">
                                        {d.name}
                                        {showSplit && (
                                            <span className="ml-2 text-[10px] text-zinc-500" data-testid={`split-${d.name.toLowerCase()}`}>
                                                <span className="text-[#002FA7]">own ₹{(d.own || 0).toFixed(2)}</span>
                                                {" + "}
                                                <span className="text-[#9C27B0]">children ₹{(d.child || 0).toFixed(2)}</span>
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-zinc-500">₹ {d.value.toFixed(2)}</div>
                                    <div className="font-semibold w-14 text-right">{pct.toFixed(1)}%</div>
                                </div>
                            );
                        })}
                        <div className="flex items-center gap-3 text-xs font-mono pt-2 border-t border-zinc-200 mt-2">
                            <div className="w-3 h-3 bg-[#84CC16]" />
                            <div className="flex-1 font-semibold">Total / piece</div>
                            <div className="font-bold">₹ {(waterfallData.find((x) => x.isTotal)?.value || 0).toFixed(2)}</div>
                        </div>
                    </div>
                </div>

                <div className="border border-zinc-200 p-6">
                    <div className="tag-pill mb-3">Investments (own + descendants)</div>
                    <h3 className="text-xl font-bold tracking-tight mb-1">Investment buildup by type × source</h3>
                    <p className="text-xs text-zinc-500 mb-4 font-mono">Stacked bar shows each investment category split by its source in the BOM tree</p>
                    <InvestmentBreakdownPanel partId={partId} testIdPrefix="cost-section-invest" />
                </div>
            </div>

            {/* Compact target comparison */}
            <div className="mt-6">
                <div className="border border-zinc-200 p-5">
                    <div className="tag-pill mb-2">Target vs Cost</div>
                    <h3 className="text-sm font-bold tracking-tight mb-3">Per-piece comparison</h3>
                    {(() => {
                        const total = (waterfallData.find((x) => x.isTotal)?.value) || 0;
                        const target = Number(part.target_price) || 0;
                        const sell = total;
                        const gap = target - sell;
                        const pct = target > 0 ? (sell / target) * 100 : 0;
                        return (
                            <div className="space-y-3">
                                <div className="flex items-end justify-between gap-3" style={{ height: 110 }}>
                                    <div className="flex-1 flex flex-col items-center justify-end h-full">
                                        <div className="text-[10px] text-zinc-500 mb-1 font-mono">SELL</div>
                                        <div className="w-full bg-[#84CC16]" style={{ height: `${Math.min(100, pct)}%` }} title={`₹ ${sell.toFixed(2)}`} />
                                        <div className="text-[11px] font-mono font-semibold mt-1">₹ {sell.toFixed(0)}</div>
                                    </div>
                                    <div className="flex-1 flex flex-col items-center justify-end h-full">
                                        <div className="text-[10px] text-zinc-500 mb-1 font-mono">TARGET</div>
                                        <div className="w-full bg-[#002FA7]" style={{ height: target > 0 ? "100%" : "0%" }} title={`₹ ${target.toFixed(2)}`} />
                                        <div className="text-[11px] font-mono font-semibold mt-1">₹ {target.toFixed(0)}</div>
                                    </div>
                                </div>
                                <div className={`text-xs font-mono ${gap >= 0 ? "text-[#2A9D8F]" : "text-[#E63946]"}`} data-testid="target-gap">
                                    {gap >= 0
                                        ? `Headroom: ₹ ${gap.toFixed(2)} (${(100 - pct).toFixed(1)}% under target)`
                                        : `Over by: ₹ ${(-gap).toFixed(2)} (${(pct - 100).toFixed(1)}% above target)`}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>

            <FinalSummary t={t} part={part} amortise={!!draft.amortise_investment} />
        </div>
    );
}

function FixedField({ label, v, onChange, testid }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</Label>
            <Input type="number" step="0.01" value={v} onChange={(e) => onChange(Number(e.target.value) || 0)} className="font-mono rounded-none" data-testid={testid} />
        </div>
    );
}

function DualField({ label, mode, valueRs, valuePct, onModeChange, onValueChange, onPctChange, derived, testid }) {
    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between">
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</Label>
                <button
                    onClick={() => onModeChange(mode === "pct" ? "value" : "pct")}
                    className="text-[10px] uppercase tracking-wider text-[#002FA7] hover:underline"
                    data-testid={`${testid}-mode-toggle`}
                >
                    {mode === "pct" ? "₹ Value" : "% Mode"}
                </button>
            </div>
            {mode === "pct" ? (
                <Input
                    type="number" step="0.01" value={valuePct}
                    onChange={(e) => onPctChange(Number(e.target.value) || 0)}
                    className="font-mono rounded-none"
                    placeholder="%"
                    data-testid={`${testid}-pct`}
                />
            ) : (
                <Input
                    type="number" step="0.01" value={valueRs}
                    onChange={(e) => onValueChange(Number(e.target.value) || 0)}
                    className="font-mono rounded-none"
                    placeholder="₹"
                    data-testid={`${testid}-value`}
                />
            )}
            <div className="text-[10px] text-zinc-500 font-mono">= ₹ {(derived || 0).toFixed(2)} / piece</div>
        </div>
    );
}

function Section({ title, action, children }) {
    return (
        <div className="border border-zinc-200">
            <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-zinc-200">
                <div className="text-sm font-semibold tracking-tight">{title}</div>
                {action}
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

function KPI({ label, value, accent, testid }) {
    return (
        <div className="border border-zinc-200 p-5" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{label}</div>
            <div className="text-3xl font-bold font-mono mt-2" style={{ color: accent }}>₹ {(Number(value) || 0).toFixed(2)}</div>
        </div>
    );
}

function FinalSummary({ t, part, amortise }) {
    const targetPrice = Number(part.target_price) || 0;
    const variance = targetPrice - t.final_cost;
    const margin = targetPrice ? (variance / targetPrice * 100) : 0;
    return (
        <div className="border-2 border-[#09090b] p-6" data-testid="final-summary">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Total cost / piece</div>
                    <div className="text-5xl font-bold font-mono">₹ {t.final_cost.toFixed(2)}</div>
                    {t.mode === "zbc" && (
                        <div className="text-[10px] uppercase tracking-wider text-[#9C27B0] font-mono mt-1">Actual Costing · Frozen @ ₹ {Number(t.frozen_price || 0).toFixed(2)}</div>
                    )}
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{t.mode === "zbc" ? "Frozen price" : "Target price"}</div>
                    <div className="text-2xl font-semibold font-mono">₹ {targetPrice.toFixed(2)}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Profit / piece</div>
                    <div className={`text-2xl font-semibold font-mono ${(t.profit_value || 0) >= 0 ? "text-[#2A9D8F]" : "text-[#E63946]"}`} data-testid="profit-value-summary">
                        ₹ {(t.profit_value || 0).toFixed(2)} ({((t.profit_value || 0) / Math.max(t.final_cost || 1, 0.0001) * 100).toFixed(1)}%)
                    </div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Variance (Target − Cost)</div>
                    <div className={`text-2xl font-semibold font-mono ${variance >= 0 ? "text-[#2A9D8F]" : "text-[#E63946]"}`} data-testid="variance-value">
                        ₹ {variance.toFixed(2)} ({margin.toFixed(1)}%)
                    </div>
                </div>
            </div>
            {!amortise && (t.total_investment || 0) > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-200 text-xs font-mono text-zinc-500">
                    Note: ₹ {t.total_investment.toLocaleString()} of investment is <strong>not</strong> included in the per-piece cost (amortisation toggle is OFF).
                </div>
            )}
        </div>
    );
}


function SubAssembliesPanel({ partId, draftId, overrides = {}, onChangeOverride }) {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [childDrafts, setChildDrafts] = useState({});
    const [tree, setTree] = useState([]);  // FULL multi-level descendant list (1, 1.1, 1.1.1, …)
    useEffect(() => {
        let cancel = false;
        const fetchT = async () => {
            setLoading(true);
            try {
                const [tr, full] = await Promise.all([
                    api.get(`/parts/${partId}/drafts/${draftId}/totals`),
                    api.get(`/parts/${partId}/bom-tree`),
                ]);
                if (cancel) return;
                const subs = tr.data.sub_assemblies || [];
                setItems(subs);
                setTotal(tr.data.sub_assemblies_total || 0);
                setTree(full.data || []);
                const uniq = Array.from(new Set(subs.map((s) => s.part_id)));
                const results = await Promise.all(uniq.map((cid) =>
                    api.get(`/parts/${cid}/drafts`).then((rr) => [cid, rr.data || []]).catch(() => [cid, []])
                ));
                if (cancel) return;
                setChildDrafts(Object.fromEntries(results));
            } catch (e) { console.warn("sub-assembly totals fetch failed:", e); }
            finally { if (!cancel) setLoading(false); }
        };
        fetchT();
        return () => { cancel = true; };
    }, [partId, draftId, overrides]);
    if (loading) return <div className="text-xs text-zinc-400 py-2 font-mono">Loading sub-assemblies…</div>;
    if (!items.length && !tree.length) return <div className="text-xs text-zinc-400 italic py-2">No child parts linked yet. Edit this part to attach child parts.</div>;

    const directCostByPartId = Object.fromEntries(items.map((s) => [s.part_id, s]));

    return (
        <>
            {/* FULL BOM TREE — shows every descendant level (1, 1.1, 1.1.1 …). Direct
                children (depth = 1) carry the per-child draft picker + unit cost.
                Deeper levels are read-only context. */}
            <table className="w-full sheet-table" data-testid="sub-assemblies-table">
                <thead><tr><th className="w-12">#</th><th className="w-12"></th><th>Part</th><th>Draft (Quotation / Actual)</th><th className="w-20">Qty/parent</th><th className="w-24">Qty/assy</th><th className="w-28">Unit Cost (₹)</th><th className="w-28">Sub-total (₹)</th><th className="w-12"></th></tr></thead>
                <tbody>
                    {tree.map((node, i) => {
                        const direct = directCostByPartId[node.part_id]; // only direct children carry per-child draft
                        const drafts = childDrafts[node.part_id] || [];
                        const overrideVal = overrides[node.part_id] || "__default__";
                        return (
                            <tr key={node.part_id || i} className={node.depth > 1 ? "bg-zinc-50" : ""} data-testid={`bom-row-${node.outline}`}>
                                <td className="font-mono text-[10px] font-semibold text-[#002FA7]">{node.outline}</td>
                                <td>
                                    {node.photo_path ? (
                                        <img src={`${API}/files/${node.photo_path}`} alt="" className="w-8 h-8 object-cover border border-zinc-200" />
                                    ) : (
                                        <div className="w-8 h-8 border border-dashed border-zinc-200" />
                                    )}
                                </td>
                                <td>
                                    <div style={{ paddingLeft: (node.depth - 1) * 16 }}>
                                        {node.depth > 1 && <span className="text-zinc-300 mr-1">↳</span>}
                                        <span className="font-medium">{node.part_name}</span>
                                        {node.part_number && <span className="text-zinc-400 ml-1 font-mono text-[11px]">({node.part_number})</span>}
                                        {node.is_assembly && <span className="ml-2 text-[9px] uppercase tracking-wider bg-[#002FA7] text-white px-1">Assy</span>}
                                    </div>
                                </td>
                                <td>
                                    {direct ? (
                                        <Select
                                            value={drafts.find((d) => d.draft_id === overrideVal) ? overrideVal : "__default__"}
                                            onValueChange={(v) => onChangeOverride && onChangeOverride(node.part_id, v === "__default__" ? "" : v)}
                                        >
                                            <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`child-draft-select-${node.part_id}`}>
                                                <SelectValue placeholder="Use default" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__default__" data-testid={`child-draft-default-${node.part_id}`}>
                                                    Use child&apos;s selected draft {direct.draft_name ? `(${direct.mode === "zbc" ? "Actual" : "Q"} · ${direct.draft_name})` : ""}
                                                </SelectItem>
                                                {drafts.map((d) => (
                                                    <SelectItem key={d.draft_id} value={d.draft_id} data-testid={`child-draft-opt-${node.part_id}-${d.draft_id}`}>
                                                        {(d.mode === "zbc" ? "Actual · " : "Q · ") + (d.name || d.draft_id)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <span className="text-[10px] text-zinc-400 italic">via {tree.find((n) => n.outline === (node.outline.split(".").slice(0, -1).join(".") || ""))?.part_name || "parent"}</span>
                                    )}
                                </td>
                                <td className="font-mono">{node.qty}</td>
                                <td className="font-mono text-zinc-500">{node.qty_cumulative}</td>
                                <td className="font-mono">{direct ? `₹ ${Number(direct.unit_cost || 0).toFixed(2)}` : "—"}</td>
                                <td className="font-mono font-medium">{direct ? `₹ ${Number(direct.total || 0).toFixed(2)}` : ""}</td>
                                <td className="text-right">
                                    <button onClick={() => navigate(`/parts/${node.part_id}/costing`)} title="Open this part's costing" className="text-zinc-400 hover:text-[#002FA7]" data-testid={`open-bom-${i}`}>
                                        <FileText size={14} />
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <div className="text-right mt-2 font-mono text-sm">
                Sub-assemblies subtotal (direct children): <span className="font-semibold">₹ {Number(total).toFixed(2)}</span>
            </div>
        </>
    );
}
